import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonCreate } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import { createCourseDateCustomAttributes } from "@shared/models/course"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { cloneDeep, get, isNil, map } from "lodash-es"
import moment from "moment"
import * as R from "remeda"
import { consolidateCourseDatesOff, constructUnitsAndDates } from "../../unit-utils"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

// TODO This needs to be relooked at

interface Payload {
  dateString: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  /**
   * Our local copy of the CardStack being created.
   *
   * The CardStack only exists in memory, not in the DB (isNewLesson is true);
   * it was created by the PhantomCardStack service.
   * Part of this action is to create it in the DB, by issuing patches
   * that are redundant locally.
   */
  cardStack: CardStack
  setCards: boolean
}

export const LESSON_FIRST_EDIT = {
  name: "LESSON_FIRST_EDIT",

  // TODO_CALENDAR
  patches(payload: Payload) {
    // Cases:
    // - the course date doesn't exist
    // - the course date exists but doesn't have a lesson Id

    // Do we send the course.calendar.dates?
    // Do we check that when
    // We also might not have a course -- so, we only check if it's used as a lesson
    // Then, we should probably just send over teh course.calendar.dates so
    // it's easier for any component whose editing the lesson to do it.
    // cases:
    // - lesson title
    // - lesson sections

    // Also, change the title from LESSON_FIRST_EDIT to something more specific like,
    // CREATE_LESSON or something like that.

    // We only call this when...
    // - it's used as a lesson. All other times, the lesson is already edited
    let oldCourseDate = R.pipe(
      payload.course.attributes.calendar.dates,
      R.filter(isCourseDateCustom),
      R.find((date) => date.attributes.dateString === payload.dateString),
    )

    let courseDatesWithLesson
    if (oldCourseDate) {
      courseDatesWithLesson = map(payload.course.attributes.calendar.dates, (date) => {
        if (
          date.type === "course-date-custom" &&
          date.attributes.dateString === payload.dateString
        ) {
          let clonedDate = cloneDeep(date)
          clonedDate.attributes.cardStackId = payload.cardStack.id
          return clonedDate
        } else {
          return date
        }
      })
    } else {
      courseDatesWithLesson = payload.course.attributes.calendar.dates.concat([
        {
          id: payload.dateString,
          type: "course-date-custom",
          attributes: createCourseDateCustomAttributes({
            dateString: payload.dateString,
            cardStackId: payload.cardStack.id,
          }),
        },
      ])
    }

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar,
    )

    let { courseDates, units } = constructUnitsAndDates(
      courseDatesWithLesson,
      consolidatedCourseDatesOff,
      payload.rotationCalendar.attributes.schoolDays,
    )

    let coursePatch
    let newCourseDate = R.pipe(
      courseDates,
      R.filter(isCourseDateCustom),
      R.find((date) => date.attributes.dateString === payload.dateString),
    )
    if ((isNil(oldCourseDate) && isNil(newCourseDate)) || newCourseDate === undefined) {
      throw Error("There isn't a course date to work with")
    }

    if (oldCourseDate) {
      coursePatch = Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: newCourseDate.id,
      })
        .set("embedded", "attributes.cardStackId", newCourseDate.attributes.cardStackId)
        .set("embedded", "attributes.unitStart", newCourseDate.attributes.unitStart)
        .set("embedded", "attributes.unitEnd", newCourseDate.attributes.unitEnd)
        .set("embedded", "attributes.unitIds", newCourseDate.attributes.unitIds)
        .inc(`attributes.calendar.version`, 1)
    } else {
      coursePatch = Patch.create("course", payload.course.id)
        .push("attributes.calendar.dates", newCourseDate)
        .inc(`attributes.calendar.version`, 1)
    }

    const cardStack = payload.cardStack
    const parentCardStack = cardStack.attributes.parentCardStack

    // Why in the world? Shouldn't this just be the default?
    let lessonPatch = Patch.create("card-stack", cardStack.id)
      .set(`meta.permissions`, [
        "can-edit-legacy-comments",
        "can-add-comments",
        "can-share-presence",
        "can-edit-plan",
        "can-manage-plan",
        "can-use-app",
        "can-view-plan",
      ])
      .set("attributes.schemaVersion", cardStack.attributes.schemaVersion)
      .set("attributes.title", "")
      .set("attributes.usedAs", "lesson")
      .set("attributes.parentCardStack", parentCardStack ?? null)
      .set("meta.isNewLesson", false)
      .set("relationships.course.data", {
        id: payload.course.id,
        type: "course",
      })
      .set("relationships.planbook.data", {
        id: payload.planbook.id,
        type: "planbook",
      })
      .isCreatePatch()

    if (payload.setCards === true) {
      const cards = cardStack.attributes.cards ?? []
      lessonPatch.set("attributes.cards", cloneDeep(cards))
      lessonPatch.set("meta.cardsUpdatingFromTemplate", false)
      if (isUnifiedCardStack(cardStack)) {
        lessonPatch
          .set("attributes.proseMirror", cloneDeep(cardStack.attributes.proseMirror))
          .set("attributes.extracted", cloneDeep(cardStack.attributes.extracted))
      }
    } else {
      // setCards is false; instead initialize them to null in our local model.
      // Except, don't overwrite the phantom cards if already present.
      // (These cases differ in whether `cards` is null or absent in MongoDB, but the backend treats those identically.
      // In either case, the local copy gets non-undefined values for these
      // properties, matching the CardStack type.)
      if (!cardStack.attributes.cards) {
        lessonPatch.set("attributes.cards", null)
        if (isUnifiedCardStack(cardStack)) {
          lessonPatch.set("attributes.proseMirror", null).set("attributes.extracted", null)
        }
      }
      lessonPatch.set("meta.cardsUpdatingFromTemplate", true)
    }

    let lessonSummaryPatch = Patch.create("card-stack-summary", cardStack.id)
      .set(`meta.permissions`, [
        "can-edit-legacy-comments",
        "can-add-comments",
        "can-share-presence",
        "can-edit-plan",
        "can-manage-plan",
        "can-use-app",
        "can-view-plan",
      ])
      .set("attributes.title", "")
      .set("meta.isNewLesson", false)
      .set("relationships.course.data", {
        id: payload.course.id,
        type: "course",
      })
      .set("relationships.planbook.data", {
        id: payload.planbook.id,
        type: "planbook",
      })
      .isCreatePatch()

    return [lessonPatch.patch, coursePatch.patch, lessonSummaryPatch.patch]
  },

  undoPatches(payload) {
    return [
      Patch.create("course", payload.course.id)
        .set("attributes.calendar.dates", cloneDeep(payload.course.attributes.calendar.dates))
        .set("attributes.calendar.units", cloneDeep(payload.course.attributes.calendar.units))
        .inc(`attributes.calendar.version`, 1).patch,
    ]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(
      { ...payload, cardStackId: payload.cardStack.id },
      findFn,
      userId,
    )
    let context = defaultLessonContext(payload, models)
    context.editType = payload.setCards ? "template" : null

    let formattedDateString = moment(payload.dateString).format("ddd, MMM. D, YYYY")
    let newEvent: LessonCreate = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      primary_object_id: payload.cardStack.id,
      date_plan_scheduled_for: payload.dateString,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "create",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,
      adverbial_verb: null,
      adverbial_object_type: null,
      adverbial_object_specifier: null,
      adverbial_object_id: null,
      adverbial_object_id_and_ancestor_ids: null,
    }

    return {
      title: "Created A lesson",
      titleForAnalytics: "Lesson - Created",
      description: `Added a lesson on ${formattedDateString} to ${get(
        models,
        "course.attributes.title",
      )}`,
      context: context,
      activity: defaultLessonActivity(payload, models, "created"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_FIRST_EDIT
